'use client';

// Shared AI Components - reusable across FinkChatWidget and marketing pages

// Brain Circuit Icon - distinctive AI icon with neural network patterns
export const BrainCircuitIcon = ({ className = '', animate = false }: { className?: string; animate?: boolean }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className={className}
  >
    {/* Brain outline */}
    <path
      d="M12 4C8.5 4 6 6.5 6 9.5C6 11 6.5 12.3 7.5 13.3C7 14.5 6.5 16 6.5 17.5C6.5 19.5 8 21 10 21C11 21 11.8 20.5 12.5 19.8C13.2 20.5 14 21 15 21C17 21 18.5 19.5 18.5 17.5C18.5 16 18 14.5 17.5 13.3C18.5 12.3 19 11 19 9.5C19 6.5 16.5 4 13 4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      className={animate ? 'animate-pulse' : ''}
    />
    {/* Circuit nodes */}
    <circle cx="9" cy="8" r="1" fill="currentColor" className={animate ? 'animate-ping' : ''} style={{ animationDuration: '2s' }} />
    <circle cx="15" cy="8" r="1" fill="currentColor" className={animate ? 'animate-ping' : ''} style={{ animationDuration: '2.5s' }} />
    <circle cx="12" cy="11" r="1.2" fill="currentColor" className={animate ? 'animate-ping' : ''} style={{ animationDuration: '1.5s' }} />
    <circle cx="10" cy="15" r="1" fill="currentColor" className={animate ? 'animate-ping' : ''} style={{ animationDuration: '3s' }} />
    <circle cx="14" cy="15" r="1" fill="currentColor" className={animate ? 'animate-ping' : ''} style={{ animationDuration: '2.2s' }} />
    {/* Circuit connections */}
    <path
      d="M9 8L12 11M15 8L12 11M12 11L10 15M12 11L14 15M10 15L14 15"
      stroke="currentColor"
      strokeWidth="0.75"
      strokeLinecap="round"
      opacity="0.6"
    />
    {/* Data flow indicators */}
    <path
      d="M6 9L4 9M18 9L20 9M6 14L3 14M18 14L21 14"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      opacity="0.4"
      strokeDasharray={animate ? "2 2" : "0"}
      className={animate ? 'animate-pulse' : ''}
    />
  </svg>
);

// Neural particle component for the orb effect
export const NeuralParticles = ({ isActive, count = 6 }: { isActive: boolean; count?: number }) => (
  <div className="absolute inset-0 overflow-hidden rounded-full">
    {[...Array(count)].map((_, i) => (
      <div
        key={i}
        className={`
          absolute w-1 h-1 rounded-full bg-white/60
          ${isActive ? 'animate-ping' : 'animate-pulse'}
        `}
        style={{
          top: `${20 + Math.sin(i * 60 * Math.PI / 180) * 30}%`,
          left: `${50 + Math.cos(i * 60 * Math.PI / 180) * 30}%`,
          animationDuration: `${1.5 + i * 0.3}s`,
          animationDelay: `${i * 0.2}s`,
        }}
      />
    ))}
  </div>
);

// AI Thinking animation - more sophisticated than dots
export const AIThinkingAnimation = ({ text = 'Fink is analyzing...' }: { text?: string }) => (
  <div className="flex items-center gap-3">
    <div className="relative w-8 h-8">
      {/* Pulsing rings */}
      <div className="absolute inset-0 rounded-full bg-gradient-to-r from-violet-500 to-cyan-500 animate-ping opacity-20" />
      <div className="absolute inset-1 rounded-full bg-gradient-to-r from-violet-500 to-cyan-500 animate-ping opacity-30" style={{ animationDelay: '0.2s' }} />
      <div className="absolute inset-0 rounded-full bg-gradient-to-r from-violet-600 to-cyan-600 flex items-center justify-center">
        <BrainCircuitIcon className="w-5 h-5 text-white" animate />
      </div>
    </div>
    <div className="flex flex-col gap-0.5">
      <span className="text-sm font-medium text-gray-700">{text}</span>
      <div className="flex gap-1">
        {['Processing', 'data', '...'].map((word, i) => (
          <span
            key={i}
            className="text-xs text-gray-400 animate-pulse"
            style={{ animationDelay: `${i * 0.3}s` }}
          >
            {word}
          </span>
        ))}
      </div>
    </div>
  </div>
);

// AI Sparkle effect for highlighting AI-powered features
export const AISparkle = ({ className = '' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <path
      d="M12 2L13.09 8.26L19 7L14.74 11.26L21 12L14.74 12.74L19 17L13.09 15.74L12 22L10.91 15.74L5 17L9.26 12.74L3 12L9.26 11.26L5 7L10.91 8.26L12 2Z"
      fill="currentColor"
      className="animate-pulse"
    />
  </svg>
);

// Gradient Text for AI-branded text
export const GradientText = ({
  children,
  className = '',
  from = 'from-violet-600',
  via = 'via-indigo-600',
  to = 'to-cyan-600'
}: {
  children: React.ReactNode;
  className?: string;
  from?: string;
  via?: string;
  to?: string;
}) => (
  <span className={`bg-gradient-to-r ${from} ${via} ${to} bg-clip-text text-transparent ${className}`}>
    {children}
  </span>
);

// Neural Mesh Background pattern
export const NeuralMeshBackground = ({ className = '' }: { className?: string }) => (
  <div className={`absolute inset-0 overflow-hidden ${className}`}>
    {/* Gradient base */}
    <div className="absolute inset-0 bg-gradient-to-br from-violet-600/5 via-indigo-600/5 to-cyan-600/5" />

    {/* Animated blobs */}
    <div className="absolute top-0 left-1/4 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl animate-pulse" />
    <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-indigo-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />

    {/* Grid pattern */}
    <div
      className="absolute inset-0 opacity-[0.02]"
      style={{
        backgroundImage: `
          linear-gradient(to right, #8b5cf6 1px, transparent 1px),
          linear-gradient(to bottom, #8b5cf6 1px, transparent 1px)
        `,
        backgroundSize: '60px 60px'
      }}
    />
  </div>
);

// Floating particles for hero sections
export const FloatingParticles = ({ count = 20 }: { count?: number }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(count)].map((_, i) => (
      <div
        key={i}
        className="absolute w-1 h-1 bg-violet-500/30 rounded-full animate-float"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animationDuration: `${3 + Math.random() * 4}s`,
          animationDelay: `${Math.random() * 2}s`,
        }}
      />
    ))}
  </div>
);
